import { ethers } from "ethers";
import etherCall from "ethers-multicall";
import { range } from "lodash";

const CHAIN_ID = 56;
const network = {
  chainId: "0x38",
  chainName: "BNB Smart Chain",
  nativeCurrency: {
    name: "BNB Smart Chain",
    symbol: "BNB",
    decimals: 18,
    wrappedToken: "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c",
  },
  rpcUrls: ["https://bsc-dataseed1.binance.org/"],
  blockExplorerUrls: ["https://bscscan.com/"],
  iconUrls: ["future"],
  isSupported: true,
};

const ABI = {
  nft: [
    {
      inputs: [
        { internalType: "address", name: "owner", type: "address" },
        { internalType: "address", name: "operator", type: "address" },
      ],
      name: "isApprovedForAll",
      outputs: [{ internalType: "bool", name: "", type: "bool" }],
      stateMutability: "view",
      type: "function",
    },
    {
      inputs: [
        { internalType: "address", name: "operator", type: "address" },
        { internalType: "bool", name: "approved", type: "bool" },
      ],
      name: "setApprovalForAll",
      outputs: [],
      stateMutability: "nonpayable",
      type: "function",
    },
    {
      inputs: [
        { internalType: "address", name: "from", type: "address" },
        { internalType: "address", name: "to", type: "address" },
        { internalType: "uint256", name: "tokenId", type: "uint256" },
      ],
      name: "safeTransferFrom",
      outputs: [],
      stateMutability: "nonpayable",
      type: "function",
    },
    {
      inputs: [
        { internalType: "address", name: "owner", type: "address" },
        { internalType: "uint256", name: "index", type: "uint256" },
      ],
      name: "tokenOfOwnerByIndex",
      outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
      stateMutability: "view",
      type: "function",
    },
    {
      inputs: [{ internalType: "address", name: "owner", type: "address" }],
      name: "balanceOf",
      outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
      stateMutability: "view",
      type: "function",
    },
    {
      inputs: [
        { internalType: "address", name: "from", type: "address" },
        { internalType: "address", name: "to", type: "address" },
        { internalType: "uint256", name: "tokenId", type: "uint256" },
      ],
      name: "transferFrom",
      outputs: [],
      stateMutability: "nonpayable",
      type: "function",
    },
  ],
  scholar: [
    {
      inputs: [
        {
          internalType: "address",
          name: "nftAddr",
          type: "address",
        },
        {
          internalType: "address",
          name: "sender",
          type: "address",
        },
      ],
      name: "valuesOf",
      outputs: [
        {
          internalType: "uint256[]",
          name: "",
          type: "uint256[]",
        },
      ],
      stateMutability: "view",
      type: "function",
    },
    {
      inputs: [
        { internalType: "address", name: "nftAddr", type: "address" },
        { internalType: "uint256[]", name: "tokenIds", type: "uint256[]" },
        { internalType: "uint256[]", name: "arrRate", type: "uint256[]" },
      ],
      name: "askBatch",
      outputs: [],
      stateMutability: "nonpayable",
      type: "function",
    },
    {
      inputs: [
        {
          internalType: "address",
          name: "nftAddr",
          type: "address",
        },
        {
          internalType: "uint256[]",
          name: "tokenIds",
          type: "uint256[]",
        },
        {
          internalType: "address",
          name: "newOwner",
          type: "address",
        },
      ],
      name: "transferOwner",
      outputs: [],
      stateMutability: "nonpayable",
      type: "function",
    },
    {
      inputs: [
        {
          internalType: "address",
          name: "nftAddr",
          type: "address",
        },
        {
          internalType: "uint256[]",
          name: "tokenIds",
          type: "uint256[]",
        },
      ],
      name: "cancelBatch",
      outputs: [],
      stateMutability: "nonpayable",
      type: "function",
    },
  ],
};

const TOKENS = {
  kuniSaru: "0x5fa891e95c948288A376C92fbd3AFc83D488d5a8",
  referral: "0x499AF777A1e8D58903c5aea447038FC0D45478A7",
  scholarship: "0x5C6c0449423f79109f080AA5679acac35CAdaCD8",
  amaGame: "0xda4089aD5a7F3B8d26c6769a56550c6A539204BD",
};

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const provider = new ethers.providers.JsonRpcProvider(network.rpcUrls[0], {
  chainId: CHAIN_ID,
});

const mProvider = new etherCall.Provider(provider, CHAIN_ID);

async function createWallet(privateKey) {
  return new ethers.Wallet(privateKey, provider);
}

function createContract(addr, abi, wallet) {
  return {
    c: new ethers.Contract(addr, abi, wallet || provider),
    m: new etherCall.Contract(addr, abi),
  };
}

async function fetchMyNft(acc) {
  const { c, m } = createContract(TOKENS.kuniSaru, ABI.nft);

  await mProvider.init();
  const balance = await c.balanceOf(acc);
  const tokenIds = await mProvider.all(
    range(0, Number(balance)).map((inx) => m.tokenOfOwnerByIndex(acc, inx))
  );
  return [balance, tokenIds];
}

const balanceNativeOf = (acc) => {
  return provider.getBalance(acc);
};

async function safeTransferFrom(wl, _to, tokenId, nonce) {
  if (!nonce) {
    nonce = await provider.getTransactionCount(wl.address);
    nonce++;
  }
  const { c } = createContract(TOKENS.kuniSaru, ABI.nft);
  const tx = await c.connect(wl).safeTransferFrom(wl.address, _to, tokenId, {
    // gasPrice: ethers.utils.parseUnits("60", "gwei"),
    // gasLimit: 3e7,
    nonce,
  });
  await tx.wait();
  await sleep(2000);
}

async function askBatch(wallet, tokenIds, rate) {
  const nft = createContract(TOKENS.kuniSaru, ABI.nft, wallet);
  const scholar = createContract(TOKENS.scholarship, ABI.scholar, wallet);
  let tx;
  if (!nft.c.isApprovedForAll(wallet.address, TOKENS.scholarship)) {
    tx = await nft.c.setApprovalForAll(TOKENS.scholarship, true);
    await tx.wait();
  }
  tx = await scholar.c.connect(wallet).askBatch(
    TOKENS.kuniSaru,
    tokenIds,
    tokenIds.map(() => rate),
    {
      gasLimit: 3e7,
    }
  );
  await tx.wait();
}

async function cancelBatch(wallet, tokenIds) {
  const scholar = createContract(TOKENS.scholarship, ABI.scholar, wallet);
  await (await scholar.c.cancelBatch(TOKENS.kuniSaru, tokenIds)).wait();
}

async function transferOwnerScholar(wallet, tokenIds, newOwner) {
  const scholar = createContract(TOKENS.scholarship, ABI.scholar, wallet);
  const tx = await scholar.c.transferOwner(TOKENS.kuniSaru, tokenIds, newOwner);
  await tx.wait();
}
function nftScholar(owner) {
  const scholar = createContract(TOKENS.scholarship, ABI.scholar);
  return scholar.c.valuesOf(TOKENS.kuniSaru, owner);
}

async function gasPrice() {
  const gasPrice = await provider.getGasPrice();
  return gasPrice;
}

export {
  network,
  createWallet,
  mProvider,
  createContract,
  fetchMyNft,
  balanceNativeOf,
  safeTransferFrom,
  provider,
  askBatch,
  cancelBatch,
  transferOwnerScholar,
  nftScholar,
  gasPrice,
};
